import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, LogOut, Package, Ticket, Link2, Mail, ClipboardList } from "lucide-react";
import api from "../../lib/api";
import { ProductsTab } from "./ProductsTab";
import { DiscountsTab } from "./DiscountsTab";
import { LinksTab } from "./LinksTab";
import { EmailsTab } from "./EmailsTab";
import { OrdersTab } from "./OrdersTab";

const TABS = [
  { key: "products", label: "Merchandise", icon: Package },
  { key: "discounts", label: "Discount Codes", icon: Ticket },
  { key: "links", label: "Promo Links", icon: Link2 },
  { key: "emails", label: "Email Marketing", icon: Mail },
  { key: "orders", label: "Orders", icon: ClipboardList },
];

const AdminDashboard = () => {
  const [tab, setTab] = useState("products");
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setAdmin(data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (checking)
    return (
      <div className="min-h-screen bg-[#0C0A09] flex items-center justify-center text-[#7A7067] font-mono text-xs uppercase tracking-widest" data-testid="admin-loading">
        Checking access...
      </div>
    );
  if (!admin) return null;

  return (
    <div className="min-h-screen bg-[#0C0A09] text-[#F5F0EB]" data-testid="admin-dashboard">
      <div className="grain-overlay" />
      <header className="border-b border-[#2C2623] bg-[#0C0A09]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#E05A36]" />
            <span className="font-display font-extrabold uppercase text-sm">
              Popular<span className="text-[#E05A36]">Rebelz</span>
              <span className="text-[#7A7067] font-mono font-normal text-[10px] tracking-[0.25em] ml-3">BACK OFFICE</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs font-mono uppercase tracking-widest text-[#7A7067] hover:text-[#E05A36] transition-colors" data-testid="admin-view-store-link">
              View store
            </a>
            <button onClick={logout} className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#B8AEA5] hover:text-[#E05A36] transition-colors" data-testid="admin-logout-button">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex gap-1 overflow-x-auto">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-widest border-b-2 transition-colors whitespace-nowrap ${
                tab === key ? "border-[#E05A36] text-[#E05A36]" : "border-transparent text-[#7A7067] hover:text-[#F5F0EB]"
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
        {tab === "products" && <ProductsTab />}
        {tab === "discounts" && <DiscountsTab />}
        {tab === "links" && <LinksTab />}
        {tab === "emails" && <EmailsTab />}
        {tab === "orders" && <OrdersTab />}
      </main>
    </div>
  );
};

export default AdminDashboard;
